<template>
    <div class="results-component">
        <div class="info-wrapper d-flex">
            <div v-if="isLoading" class="progress-wrapper">
                <v-progress-circular
                    indeterminate
                    color="primary"
                    :size="50"
                    :width="5"
                ></v-progress-circular>
            </div>
            <div v-if="!totalRecordCount && !isLoading" class="info d-flex flex-column justify-center mt-4 pt-16 pb-16 px-md-16 px-4">
                <div class="px-md-10 px-4">
                    <div class="header text-left mb-6 invicta">
                        {{$t('results.no_results_header')}}
                        <Tooltip
                            :text="$t('results.no_results_tooltip_text')"
                            :icon="true"
                            :class="['tooltip-position', this.$vuetify.display.mobile ? 'tooltip-mobile' : '' ]">
                        </Tooltip>
                    </div>
                    <div class="subheader text-left">
                        {{$t('results.no_results_change_filters_or_subheader')}}
                        <a href="https://bank.invicta.pl/kontakt/" target="_blank" class="link">
                            {{$t('results.no_results_contact_us_subheader_link')}}
                        </a>.
                    </div>
                </div>
            </div>
        </div>
        <div class="results_wrapper">
            <div 
                v-if="results.filter(r => r.perfect_match && r.perfect_match[0]).length > 0"
                :class="['header py-8 mx-md-12 mx-6 text-left', $vuetify.display.mobile ? 'mobile' : '']"
                v-html="$t('results.counter_phenotype', {count: perfectRecordCount })">
            </div>
            <v-row class="ma-0" v-if="results.filter(r => r.perfect_match && r.perfect_match[0]).length > 0">
                <v-col>
                    <result-component
                        :class="['mb-8', isLoading ? 'blur' : '']"
                        v-for="(result, index) in results.filter(r => r.perfect_match && r.perfect_match[0])"
                        :phenotypeData="result"
                        :compatible="true"
                        :key="'r' + index"
                    />
                </v-col>
            </v-row>
        </div>

        <div v-if="totalRecordCount > 0">
            <div class="results_wrapper mt-md-12 mt-2 pt-2">
                <div  v-if="searchForSimilar && results.filter(r => !r.perfect_match || !r.perfect_match[0]).length > 0"
                    :class="[
                        'header py-8 mx-md-12 mx-6 text-left',
                        $vuetify.display.mobile ? 'mobile' : '',
                        'd-flex flex-column justify-start align-start'
                        ]"
                >
                    <div>{{ $t("donor.phenotype.similar_profiles") }}</div>
                    <div class="subheader mt-4">
                        {{ $t("donor.phenotype.similar_profiles_label") }}
                    </div>
                </div>
                <div v-if="searchForSimilar">
                    <v-row class="ma-0">
                        <v-col>
                            <result-component
                                :class="['mb-4', isLoading ? 'blur' : '']"
                                v-for="(result, index) in results.filter(r => !r.perfect_match || !r.perfect_match[0])"
                                :phenotypeData="result"
                                :compatible="false"
                                :key="'r' + index"
                            />
                        </v-col>
                    </v-row>
                </div>
                <div v-else>
                    <v-row class="ma-0">
                        <v-col>
                            <result-component
                                :class="['mb-4', isLoading ? 'blur' : '']"
                                v-for="(result, index) in results"
                                :phenotypeData="result"
                                :compatible="false"
                                :key="'r' + index"
                            />
                        </v-col>
                    </v-row>
                </div>
                <v-row class="mb-9 mt-4 px-12" v-if="!this.$vuetify.display.mobile && paginationLength">
                    <v-col cols="9">
                        <v-pagination
                            v-if="totalRecordCount > pageSize"
                            v-model="page"
                            color="primary"
                            :length="paginationLength"
                            total-visible="6"
                            @update:modelValue="scrollToTop"
                        ></v-pagination>
                    </v-col>
                    <v-col cols="2" class="px-0 text-right">
                        <div class="results-page-size-label mt-3 pr-2">{{$t('results.per_page_label')}}:</div>
                    </v-col>
                    <v-col cols="1" class="pl-0 pr-6">
                        <div class="float-left mt-1 mb-16 w-100">
                            <CustomSelect
                                class="selected-page-size"
                                v-model="selectedPageSize"
                                :multiple="false"
                                :flat="true"
                                size="small"
                                :chip="false"
                                :options="[{key: 5}, {key: 10}, {key: 20}, {key: 50}]"/>
                        </div>
                    </v-col>
                </v-row>
            </div>
            <v-chip 
                v-if="this.$vuetify.display.mobile"
                class="results-chip" 
                size="x-small" 
                label 
                color="primary"
            >{{results.length}}/{{totalRecordCount}}</v-chip>
            <v-progress-linear
                v-if="this.$vuetify.display.mobile"
                :active="morePageToLoad()"
                indeterminate
                color="primary"
                v-intersect.quiet="loadNextPage"
            ></v-progress-linear>
        </div>
    </div>
</template>

<script>
import ResultComponent from './ResultComponent.vue';
import CustomSelect from "./CustomSelectComponent";
import { Tooltip } from "design-system-ui-components";
import { useStore } from "vuex";
import { computed } from 'vue';
import axios from "axios";

export default {
    components: {
        ResultComponent,
        CustomSelect,
        Tooltip
    },
    setup() {
        const store = useStore()

        return {
            pageSize: computed(() => store.getters['resultsModule/getPageSize']),
            pageLoaded: computed(() => store.getters['resultsModule/getPageLoaded']),
            totalRecordCount: computed(() => store.getters['resultsModule/getTotalRecordCount']),
            perfectRecordCount: computed(() => store.getters['resultsModule/getPerfectMatchCount']),
            paginationLength: computed(() => store.getters['resultsModule/getPaginationLength']),
            isLoading: computed(() => store.getters['resultsModule/getLoading']),
            searchForSimilar: computed(() => store.getters['resultsModule/getSearchForSimilar']),
            results: computed(() => store.getters['resultsModule/getResults']),
            filters: computed(() => store.getters['filtersModule/getFilters']),
            materialType: computed(() => store.getters['filtersModule/getMaterialType']),
            store
        }
    },
    data: () => {
        return {
            // we prepare for future input of merketing content,
            // not used right now
            bannerContent: `
                <div>MARKETING JEST SUPER</div><br>
                <div>JEDZ WARZYWA</div>
            `,
            showBanner: true,
            resetToFirstPage: false,
            page: 1,
            selectedPageSize: [],
        }
    },
    created() {
        this.selectedPageSize = [{key: this.pageSize}],
        this.store.dispatch('resultsModule/clearResults');
        this.$watch('selectedPageSize', (pageSize) => {
            this.store.dispatch('resultsModule/setPageSize', pageSize[0].key)
            this.getPageResults(1, true)
        }),
        this.$watch('page', (page) => {
            if (page==1 && this.resetToFirstPage) {
                this.resetToFirstPage = false
            } else {
                this.getPageResults(page, true)
            }
        }),
        this.$watch('filters', () => {
            this.resetToFirstPage = true
            this.page = 1
        })
    },
    methods: {
        resultsCountDeclination(count) {
            if(count == 1) {
                return this.$t('results.nom');
            } else if(count <= 4) {
                return this.$t('results.nom_plural');
            } else if(count > 4 && count < 22) {
                return this.$t('results.gen_plural');
            } else {
                if(
                count || 10 == 2 || 
                count || 10 == 3 || 
                count || 10 == 4
                ){
                return this.$t('results.nom_plural')
                } else {
                return this.$t('results.gen_plural');
                }
            }
        },
        morePageToLoad() {
            return Math.ceil(this.totalRecordCount / this.pageSize) > this.pageLoaded
        },
        getPageResults(page, replaceResults = false) {
            if (replaceResults) {
                this.store.dispatch('resultsModule/setLoading', true)
            }
            let request;
            if(this.store.getters['resultsModule/getSearchForSimilar']) {
                request = axios
                    .get(`/api/donor/find_matching?material_type=${this.materialType}&limit=${this.pageSize}&offset=${((page-1)*this.pageSize)}`)
            } else {
                request = axios
                    .post(`/api/donor/${this.materialType}/data`, {
                        "from": ((page-1)*this.pageSize),
                        "size": this.pageSize,
                        "_source": true,
                        "script_fields": [
                        "age",
                        "max_true_reservation_count",
                        "public_donor_id"
                        ],
                        "filters": this.filters
                    })
            }
            request
                .then((response) => {
                    response.data.data.forEach(e => {
                        if(!e.fields.mismatch_factor) {
                          e.fields.mismatch_factor = [1000000]
                        }
                    })
                    if (replaceResults) {
                        this.store.dispatch('resultsModule/setResults', response.data.data.map(e => ({...e._source, ...e.fields})))
                    } else {
                        response.data.data.map(e => ({...e._source, ...e.fields})).forEach((item) => this.results.push(item))
                    }
                })
                .catch(err => {
                    console.error(err);
                })
                .finally(() => {
                    this.store.dispatch('resultsModule/setPageLoaded', page)
                    this.store.dispatch('resultsModule/setLoading', false)
                })
        },
        loadNextPage(entries) {
            let nextPage = this.pageLoaded + 1
            if (entries && this.morePageToLoad()) {
                this.getPageResults(nextPage)
            }
        },
        scrollToTop() {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }
}
</script>

<style lang="scss" scoped>
@import "../../node_modules/design-system-ui-components/dist/all.min.css";

.selected-page-size {
    width: 90px;
}

.results_wrapper {
    background: transparent;
}

@media (min-width: 960px) {
    .results_wrapper {
        background: white;
    }
}

.tooltip-position {
    margin-top: 8px;
    margin-left: 8px;
}
.tooltip-mobile {
  max-width: 24px;
}
.v-col {
    padding: 0 !important;
}
.v-row {
    margin-left: 0 !important;
    margin-right: 0 !important;
}
.blur {
    filter: blur(2px);
}
:deep(.link) {
	text-decoration: underline !important;
    font-weight: bold;
}
.results-count {
    @include nsb-font-header;
    font-size: 14px;
    line-height: 21px;
    // width: 100%;
    text-align: left;
    .bold {
        @include nsb-font-header-bold;
        line-height: 21px;
    }
}
.results-chip {
    position: fixed;
    bottom: 8px;
    left: 8px;
}
.results-page-size-label {
    @include nsb-font-container;
    font-size: 14px;
}
.results-component {
    .header {
        @include nsb-gentium-bold;
        color: var(--ds-primary, #00205B);
        /* Desktop/Title-01 */
        font-family: Gentium Basic;
        font-size: 40px;
        font-style: normal;
        // font-weight: 700;
        line-height: 130%; /* 52px */
        &.mobile {
        font-size: 28px;
        display: flex;
        align-items: center;
        }
    }
    .subheader {
        @include nsb-font-header;
        color: var(--nsb-color-1, #0E346F);
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 23px; /* 164.286% */
    }
    position: relative;
    .info-wrapper {
        position: relative;
        .progress-wrapper {
            width: 100%;
            height: 200px;
            display: flex;
            flex-direction: row;
            justify-content: center;
            align-items: center;
            .v-progress-circular {
                z-index: 999;
            }
        }
        .info {
            box-sizing: border-box;
            width: 100%;
            // height: 350px;
            background: #FCFCFF;
            border: 1px solid $nsb-color-3;
            border-radius: 4px;
            .header {
                @include nsb-gentium-bold;
                font-size: 23px !important;
                line-height: 27px;
            }
            .subheader {
                @include nsb-font-header;
                font-size: 14px !important;
                line-height: 21px;
            }
        }
    }
}

:deep(.v-pagination__item) {
    margin-right: 0px;
    .v-btn {
        @include nsb-font-button-regular;
        font-size: 14px !important;
        line-height: 21px !important;
        max-height: 40px;
        max-width: 40px;
    }
    &.v-pagination__item--is-active {
        .v-btn {
            background-color: $ds-primary !important;
            color: $ds-white !important;
            .v-btn__overlay {
                opacity: 0;
            }
        }
    }
}
:deep(.v-pagination__prev) {
    margin-right: 8px;
    .v-btn {
        font-size: 14px !important;
        max-height: 40px;
        max-width: 40px;
    }
}
:deep(.v-pagination__next) {
    margin-left: 12px;
    .v-btn {
        font-size: 14px !important;
        max-height: 40px;
        max-width: 40px;
    }
}

</style>